import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import { tKey } from 'ember-intl';
import type { GeneralDatepickersDatepickerWithSidebarSignature } from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import type { MetricType } from 'lumen/models/market-performance/data-item';
import type MarketSnapshotsOverviewRoute from 'lumen/routes/market/snapshots/overview';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import {
  type DataPreferences,
  DEFAULT_DATA_PREFERENCES,
  DEFAULT_TABLE_LAYOUT_PREFERENCES,
  GROUP_BY,
  type GroupBy,
  isGroupBy,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import { formatMonthDateRange } from 'lumen/utils/dates/format-month-date-range';
import {
  buildApiOrderBy,
  remapOrderByMetric,
} from 'lumen/utils/market-snapshots-order-by';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketSnapshotsOverviewController extends Controller {
  declare model: ModelFrom<MarketSnapshotsOverviewRoute>;

  queryParams = [
    'fromDate',
    'toDate',
    'groupBy',
    'orderBy',
    'page',
    'pageSize',
  ];

  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;

  @service declare intl: Services['intl'];
  @service('local-storage') declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];

  @tracked dataPreferences: DataPreferences = DEFAULT_DATA_PREFERENCES;
  @tracked fromDate?: string;
  @tracked groupBy: GroupBy = GROUP_BY.PROPERTY;
  // Semantic sort: "field" ascending, "-field" descending (e.g. "otb.totals.revenue").
  // The route derives the API order_by from this plus sortOn/variance.
  @tracked orderBy = 'name';
  @tracked page = 1;
  @tracked pageSize = 25;
  @tracked tableLayoutPreferences: TableLayoutPreferences =
    DEFAULT_TABLE_LAYOUT_PREFERENCES;
  @tracked toDate?: string;

  pageSizeOptions = [25, 50, 100, 250];

  get currentPageSizeOption(): { label: string; value: number } {
    return {
      value: this.pageSize,
      label: this.intl.t('general.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(this.pageSize),
      }),
    };
  }

  get datepickerSidebarLists(): GeneralDatepickersDatepickerWithSidebarSignature['Args']['sidebarLists'] {
    const today = this.hotelAndMarketService.getLocalTime();

    const thisMonth = {
      id: 'this-month',
      text: tKey('general.this_month'),
      range: { start: today.startOf('month'), end: today.endOf('month') },
    };
    const thisYear = {
      id: 'this-year',
      text: tKey('general.this_year'),
      range: { start: today.startOf('year'), end: today.endOf('year') },
    };
    const custom = {
      id: 'custom',
      text: tKey('general.custom'),
      range: { start: today.startOf('month'), end: today.endOf('month') },
    };

    const options = this.isMonthView
      ? [
          thisMonth,
          {
            id: 'next-12-months',
            text: tKey('general.next_12_months'),
            range: {
              start: today.startOf('month'),
              end: today.add(11, 'month').endOf('month'),
            },
          },
          thisYear,
          custom,
        ]
      : [
          {
            id: 'today',
            text: tKey('general.today'),
            range: { start: today, end: today },
          },
          {
            id: 'yesterday',
            text: tKey('general.yesterday'),
            range: {
              start: today.subtract(1, 'day'),
              end: today.subtract(1, 'day'),
            },
          },
          {
            id: 'this-week',
            text: tKey('general.this_week'),
            range: { start: today.startOf('week'), end: today.endOf('week') },
          },
          thisMonth,
          thisYear,
          custom,
        ];

    return [{ title: this.intl.t('general.selected_period'), options }];
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    const today = this.hotelAndMarketService.getLocalTime();

    return {
      start: this.fromDate
        ? this.hotelAndMarketService.toLocalTime(this.fromDate)
        : today.startOf('month'),
      end: this.toDate
        ? this.hotelAndMarketService.toLocalTime(this.toDate)
        : today.endOf('month'),
    };
  }

  get isMonthView(): boolean {
    return this.groupBy === GROUP_BY.MONTH;
  }

  get maxDate(): Dayjs {
    const today = this.hotelAndMarketService.getLocalTime();
    return today.add(2, 'year').endOf('year');
  }

  get showingText(): string {
    const totalRecords = this.totalRecords ?? 0;

    if (totalRecords === 0) {
      return '';
    }

    const startIndex = (this.page - 1) * this.pageSize + 1;
    const endIndex = Math.min(this.page * this.pageSize, totalRecords);

    return this.intl.t('general.page_showing', {
      currentPage: this.intl.formatNumber(startIndex),
      lastPage: this.intl.formatNumber(endIndex),
      totalRecords: this.intl.formatNumber(totalRecords),
    });
  }

  get totalRecords(): number | null {
    const metaCount =
      this.model.loadDataItemsTask.lastSuccessful?.value?.meta?.count;

    if (metaCount === undefined) {
      return null;
    }

    return metaCount;
  }

  get visibleMetricTypes(): MetricType[] {
    return [
      'otb',
      this.dataPreferences.sdlyColumn,
      'ly',
      this.dataPreferences.forecastColumn,
      'budget',
    ];
  }

  @action
  monthTriggerLabelFormatter(start: Dayjs, end: Dayjs): string {
    return formatMonthDateRange(
      start,
      end,
      this.dates.partialDateFormat.YEAR_AND_MONTH_LONG,
    );
  }

  @action
  onChangePageSize(pageSizeOption: { label: string; value: number }): void {
    this.pageSize = pageSizeOption.value;
    this.page = 1;
  }

  @action
  onSort(orderBy: string): void {
    this.orderBy = orderBy;
    this.page = 1;
  }

  @action
  setGroupBy(groupBy: string): void {
    // orderBy stays in the URL on purpose so switching back restores the sort.
    // Date snapping happens in the transition validator, not here.
    if (!isGroupBy(groupBy)) {
      return;
    }

    this.groupBy = groupBy;
    this.page = 1;
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  updateDataPreferences(preferences: DataPreferences): void {
    // sdlyColumn and forecast change the API request;
    // variance changes the API order_by suffix if sortOn=variance.
    const sdlyColumnChanged =
      preferences.sdlyColumn !== this.dataPreferences.sdlyColumn;
    const forecastChanged =
      preferences.forecastColumn !== this.dataPreferences.forecastColumn;

    // A sort on a swapped column would otherwise be left pointing at a metric
    // that's no longer requested; repoint it to the equivalent new column.
    let orderBy = this.orderBy;
    if (sdlyColumnChanged) {
      orderBy = remapOrderByMetric(
        orderBy,
        this.dataPreferences.sdlyColumn,
        preferences.sdlyColumn,
      );
    }
    if (forecastChanged) {
      orderBy = remapOrderByMetric(
        orderBy,
        this.dataPreferences.forecastColumn,
        preferences.forecastColumn,
      );
    }

    const { sortOn } = this.tableLayoutPreferences;
    const apiOrderByChanged =
      buildApiOrderBy(orderBy, sortOn, this.dataPreferences.variance) !==
      buildApiOrderBy(orderBy, sortOn, preferences.variance);

    this.dataPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_DATA_PREFERENCES,
      JSON.stringify(preferences),
    );

    if (orderBy !== this.orderBy) {
      // Setting a refreshModel QP (orderBy/page) refreshes the model on its own;
      // also calling router.refresh would abort that transition and revert it.
      this.orderBy = orderBy;
      this.page = 1;
    } else if (apiOrderByChanged) {
      if (this.page !== 1) {
        // Resetting page (a refreshModel QP) refetches; router.refresh would abort it.
        this.page = 1;
      } else {
        this.router.refresh('market.snapshots.overview');
      }
    } else if (sdlyColumnChanged || forecastChanged) {
      // The request changed but the URL didn't; refetch explicitly.
      this.router.refresh('market.snapshots.overview');
    }
  }

  @action
  updateDateRange(range: { end: Dayjs | null; start: Dayjs }): void {
    const fromDate = range.start.format('YYYY-MM-DD');
    const toDate = (range.end ?? range.start).format('YYYY-MM-DD');

    this.fromDate = fromDate;
    this.toDate = toDate;
  }

  @action
  updateTableLayoutPreferences(preferences: TableLayoutPreferences): void {
    const sortOnChanged =
      preferences.sortOn !== this.tableLayoutPreferences.sortOn;

    this.tableLayoutPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TABLE_LAYOUT,
      JSON.stringify(preferences),
    );

    // sortOn change resets sort; the orderBy/page QP change refetches.
    if (sortOnChanged) {
      this.onSort('name');
    }
  }
}
